import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, ListOrdered, LogOut, Maximize, RotateCcw, SlidersHorizontal, Type, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fromNow } from "@/lib/time";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { WallGrid } from "@/components/wallboard/SortableGrid";
import { CallStats } from "@/components/wallboard/CallStats";
import { OverdueBanner } from "@/components/wallboard/OverdueBanner";
import { Switch } from "@/components/ui/switch";
import { useAutoScroll } from "@/hooks/useAutoScroll";
import { useSettings } from "@/context/SettingsContext";
import { FunStrip } from "@/components/wallboard/FunStrip";
import { WallDetail } from "@/components/wallboard/WallDetail";
import { CompletionParty } from "@/components/wallboard/CompletionParty";

const isNight = (w, now) => {
  if (!w?.night_mode) return false;
  const h = Number(now.toLocaleTimeString("en-GB", { hour: "2-digit", timeZone: "Europe/London" }).slice(0, 2));
  return w.night_from > w.night_to ? h >= w.night_from || h < w.night_to : h >= w.night_from && h < w.night_to;
};

const useClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(t);
  }, []);
  return now;
};

const BarButton = ({ children, className = "", ...props }) => (
  <button {...props} className={`flex h-9 items-center gap-2 rounded-full border px-3.5 text-sm font-semibold transition-colors hover:bg-white/5 ${className}`}>{children}</button>
);

const useScale = (key, initial) => {
  const [scale, setScale] = useState(() => Number(localStorage.getItem(key)) || initial);
  const change = ([v]) => {
    setScale(v);
    localStorage.setItem(key, String(v));
  };
  return [scale, change];
};

const SizeSlider = ({ label, value, onChange, testid }) => (
  <div className="flex w-full items-center gap-3" title={`${label} text size`}>
    <span className="flex w-14 shrink-0 items-center gap-1 text-xs font-semibold text-muted-foreground"><Type className="h-3.5 w-3.5" /> {label}</span>
    <Slider min={0.8} max={2.4} step={0.05} value={[value]} onValueChange={onChange} data-testid={`${testid}-slider`} />
    <span className="w-10 text-right font-mono text-xs text-muted-foreground" data-testid={`${testid}-value`}>{Math.round(value * 100)}%</span>
  </div>
);

export default function Wallboard() {
  const { user, logout } = useAuth();
  const now = useClock();
  const wb = useSettings()?.team?.wallboard;
  const [data, setData] = useState(null);
  const [scale, changeScale] = useScale("wb-scale", 1.25);
  const [callScale, changeCallScale] = useScale("wb-call-scale", 1.25);
  const [ordering, setOrdering] = useState(false);
  const [openId, setOpenId] = useState(null);
  const closeDetail = useCallback(() => setOpenId(null), []);
  const [autoScroll, setAutoScroll] = useState(() => localStorage.getItem("wb-autoscroll") !== "0");
  useAutoScroll(autoScroll && !ordering && !openId);
  const toggleAutoScroll = (v) => {
    setAutoScroll(v);
    localStorage.setItem("wb-autoscroll", v ? "1" : "0");
  };
  const [draft, setDraft] = useState([]);

  const load = useCallback(() => api.get("/wallboard").then((r) => setData(r.data)).catch(() => {}), []);

  useEffect(() => {
    load();
    if (ordering) return undefined;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load, ordering]);

  const startOrdering = () => {
    setDraft(data?.items || []);
    setOrdering(true);
  };
  const saveOrder = async (keys) => {
    try {
      await api.put("/wallboard/order", { keys });
      toast.success(keys.length ? "Priority order saved" : "Priority reset to due dates");
      setOrdering(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const fullscreen = () => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen?.());

  const items = ordering ? draft : data?.items || [];
  const counts = (type) => (data?.items || []).filter((i) => i.type === type).length;
  const isScreen = user.role === "screen";

  return (
    <div className={`min-h-screen ${isNight(wb, now) ? "night-mode" : ""}`} data-testid="wallboard-page">
      <header className="sticky top-0 z-30 border-b bg-background/90 px-4 py-2.5 backdrop-blur-xl lg:px-6">
        <div className="flex items-center gap-4">
          <div className="hidden shrink-0 flex-col items-center md:flex" style={{ fontSize: `${callScale * 16}px` }} data-testid="wallboard-clock">
            <img src="/atom-logo.png" alt="Atom IP" className={`${wb?.logo_spin === false ? "" : "orbit"} h-[2.4em] w-[2.4em] object-contain`} data-testid="wallboard-logo" />
            <p className="mt-[0.15em] font-mono text-[1.15em] font-semibold leading-none">{now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" })}</p>
            <p className="mt-[0.2em] font-mono text-[0.5em] uppercase tracking-wider text-muted-foreground">{now.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "Europe/London" })}</p>
          </div>
          <div className="min-w-0 flex-1" style={{ fontSize: `${callScale * 16}px` }}>
            <CallStats />
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <Popover>
              <PopoverTrigger asChild>
                <BarButton data-testid="wallboard-display-settings" title="Text size" className="w-9 justify-center px-0"><SlidersHorizontal className="h-4 w-4" /></BarButton>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 space-y-4" data-testid="wallboard-display-panel">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Text size</p>
                <SizeSlider label="Calls" value={callScale} onChange={changeCallScale} testid="wallboard-call-size" />
                <SizeSlider label="Tasks" value={scale} onChange={changeScale} testid="wallboard-text-size" />
                <label className="flex items-center justify-between gap-3 border-t pt-3 text-sm">
                  <span>Auto-scroll long lists</span>
                  <Switch checked={autoScroll} onCheckedChange={toggleAutoScroll} data-testid="wallboard-autoscroll-switch" />
                </label>
              </PopoverContent>
            </Popover>
            {ordering ? (
              <>
                <BarButton onClick={() => saveOrder([])} data-testid="wallboard-reset-order" title="Clear manual order" className="w-9 justify-center px-0"><RotateCcw className="h-4 w-4" /></BarButton>
                <BarButton onClick={() => setOrdering(false)} data-testid="wallboard-cancel-order" title="Cancel" className="w-9 justify-center px-0"><X className="h-4 w-4" /></BarButton>
                <button onClick={() => saveOrder(draft.map((i) => i.key))} data-testid="wallboard-save-order" className="flex h-9 items-center gap-1.5 rounded-full bg-emerald-400 px-4 text-sm font-bold text-black transition-colors hover:bg-emerald-300">
                  <Check className="h-4 w-4" /> Save
                </button>
              </>
            ) : (
              <button onClick={startOrdering} disabled={!data?.items?.length} data-testid="wallboard-order-priority" title="Order priority" className="flex h-9 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-white disabled:opacity-50">
                <ListOrdered className="h-4 w-4" /> <span className="hidden xl:inline">Priority</span>
              </button>
            )}
            <BarButton onClick={fullscreen} data-testid="wallboard-fullscreen" title="Full screen" className="w-9 justify-center px-0"><Maximize className="h-4 w-4" /></BarButton>
            {isScreen ? (
              <BarButton onClick={logout} data-testid="wallboard-logout" title="Sign out" className="w-9 justify-center px-0"><LogOut className="h-4 w-4" /></BarButton>
            ) : (
              <Link to="/" data-testid="wallboard-back" title="Back to dashboard" className="flex h-9 w-9 items-center justify-center rounded-full border transition-colors hover:bg-white/5"><ArrowLeft className="h-4 w-4" /></Link>
            )}
          </div>
        </div>
      </header>
      <main className="px-6 py-5 lg:px-10">
        <div style={{ fontSize: `${scale * 16}px` }}>
          <FunStrip />
          <OverdueBanner items={data?.items || []} />
        </div>
        {ordering && (
          <p className="fade-up mb-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-200" data-testid="wallboard-ordering-hint">
            Drag the cards into priority order (#1 = most important), then hit Save.
          </p>
        )}
        <div className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted-foreground">
          <span className="font-semibold uppercase tracking-[0.2em]">Priority list</span>
          <span data-testid="wallboard-counts">
            <b className="font-mono text-blue-300">{counts("reminder")}</b> reminders · <b className="font-mono text-amber-300">{counts("project")}</b> projects · <b className="font-mono text-pink-300">{counts("thought")}</b> to discuss
          </span>
          {data?.updated_by && !ordering && (
            <span className="ml-auto" data-testid="wallboard-order-meta">Order set by {data.updated_by} {fromNow(data.updated_at)}</span>
          )}
        </div>
        <div style={{ fontSize: `${scale * 16}px` }}>
          {!data ? (
            <div className="h-40 animate-pulse rounded-2xl bg-white/[0.04]" />
          ) : items.length === 0 ? (
            <p className="py-24 text-center font-display text-[1.6em] text-muted-foreground" data-testid="wallboard-empty">All clear. Nothing open right now.</p>
          ) : (
            <WallGrid items={items} ordering={ordering} onReorder={setDraft} onOpen={setOpenId} />
          )}
        </div>
      </main>
      {openId && <WallDetail rid={openId} scale={scale} onClose={closeDetail} />}
      <CompletionParty items={data?.just_completed} w={wb} />
    </div>
  );
}
